import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './DotsAndBoxes.css';

const GRID_SIZE = 4; // 4x4 grid of boxes (5x5 dots)

const DotsAndBoxes = () => {
  const [horizontalLines, setHorizontalLines] = useState(
    Array(GRID_SIZE + 1).fill().map(() => Array(GRID_SIZE).fill(false))
  );
  const [verticalLines, setVerticalLines] = useState(
    Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE + 1).fill(false))
  );
  const [boxes, setBoxes] = useState(
    Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(0)) // 0 = empty, 1 = player1, 2 = player2
  );
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [scores, setScores] = useState({ player1: 0, player2: 0 });
  const [gameOver, setGameOver] = useState(false);
  const [gameActive, setGameActive] = useState(false);

  const startGame = () => {
    setHorizontalLines(Array(GRID_SIZE + 1).fill().map(() => Array(GRID_SIZE).fill(false)));
    setVerticalLines(Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE + 1).fill(false)));
    setBoxes(Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(0)));
    setCurrentPlayer(1);
    setScores({ player1: 0, player2: 0 });
    setGameOver(false);
    setGameActive(true);
  };

  const checkBoxCompletion = (newHorizontal, newVertical) => {
    const newBoxes = boxes.map(row => [...row]);
    let boxesCompleted = 0;

    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        if (newBoxes[row][col] === 0) { // Only check empty boxes
          const hasTop = newHorizontal[row][col];
          const hasBottom = newHorizontal[row + 1][col];
          const hasLeft = newVertical[row][col];
          const hasRight = newVertical[row][col + 1];

          if (hasTop && hasBottom && hasLeft && hasRight) {
            newBoxes[row][col] = currentPlayer;
            boxesCompleted++;
          }
        }
      }
    }

    return { newBoxes, boxesCompleted };
  };

  const handleHorizontalLineClick = (row, col) => {
    if (!gameActive || horizontalLines[row][col]) return;

    const newHorizontal = horizontalLines.map(r => [...r]);
    newHorizontal[row][col] = true;
    setHorizontalLines(newHorizontal);

    const { newBoxes, boxesCompleted } = checkBoxCompletion(newHorizontal, verticalLines);
    setBoxes(newBoxes);

    if (boxesCompleted > 0) {
      // Player gets another turn and points
      setScores(prev => ({
        ...prev,
        [`player${currentPlayer}`]: prev[`player${currentPlayer}`] + boxesCompleted
      }));
    } else {
      // Switch players
      setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
    }

    // Check if game is over
    const totalBoxes = GRID_SIZE * GRID_SIZE;
    const completedBoxes = newBoxes.flat().filter(box => box !== 0).length;
    if (completedBoxes === totalBoxes) {
      setGameOver(true);
      setGameActive(false);
    }
  };

  const handleVerticalLineClick = (row, col) => {
    if (!gameActive || verticalLines[row][col]) return;

    const newVertical = verticalLines.map(r => [...r]);
    newVertical[row][col] = true;
    setVerticalLines(newVertical);

    const { newBoxes, boxesCompleted } = checkBoxCompletion(horizontalLines, newVertical);
    setBoxes(newBoxes);

    if (boxesCompleted > 0) {
      // Player gets another turn and points
      setScores(prev => ({
        ...prev,
        [`player${currentPlayer}`]: prev[`player${currentPlayer}`] + boxesCompleted
      }));
    } else {
      // Switch players
      setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
    }

    // Check if game is over
    const totalBoxes = GRID_SIZE * GRID_SIZE;
    const completedBoxes = newBoxes.flat().filter(box => box !== 0).length;
    if (completedBoxes === totalBoxes) {
      setGameOver(true);
      setGameActive(false);
    }
  };

  const getWinner = () => {
    if (scores.player1 > scores.player2) return 'Player 1';
    if (scores.player2 > scores.player1) return 'Player 2';
    return 'Tie';
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">⬜ Dots and Boxes</h1>
        <div className="game-instructions">
          Click on lines to connect dots. Complete boxes to score points!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          <div className="score-line">
            <span>Player 1: {scores.player1}</span>
            <span>Player 2: {scores.player2}</span>
          </div>
          {gameActive && (
            <div className="current-player">Current: Player {currentPlayer}</div>
          )}
          {gameOver && (
            <div className="winner-display">🎉 Winner: {getWinner()}!</div>
          )}
        </div>
        
        <div className="game-play-area">
          <div className="dots-and-boxes-container">
            <svg className="dots-svg" viewBox="0 0 320 320" width="320" height="320">
              {/* Draw dots */}
              {Array(GRID_SIZE + 1).fill().map((_, row) =>
                Array(GRID_SIZE + 1).fill().map((_, col) => (
                  <circle
                    key={`dot-${row}-${col}`}
                    cx={col * 64 + 32}
                    cy={row * 64 + 32}
                    r="4"
                    fill="#333"
                  />
                ))
              )}
              
              {/* Draw horizontal lines */}
              {horizontalLines.map((row, rowIndex) =>
                row.map((isActive, colIndex) => (
                  <line
                    key={`h-${rowIndex}-${colIndex}`}
                    x1={colIndex * 64 + 40}
                    y1={rowIndex * 64 + 32}
                    x2={(colIndex + 1) * 64 + 24}
                    y2={rowIndex * 64 + 32}
                    stroke={isActive ? "#333" : "#ddd"}
                    strokeWidth="4"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleHorizontalLineClick(rowIndex, colIndex)}
                  />
                ))
              )}
              
              {/* Draw vertical lines */}
              {verticalLines.map((row, rowIndex) =>
                row.map((isActive, colIndex) => (
                  <line
                    key={`v-${rowIndex}-${colIndex}`}
                    x1={colIndex * 64 + 32}
                    y1={rowIndex * 64 + 40}
                    x2={colIndex * 64 + 32}
                    y2={(rowIndex + 1) * 64 + 24}
                    stroke={isActive ? "#333" : "#ddd"}
                    strokeWidth="4"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleVerticalLineClick(rowIndex, colIndex)}
                  />
                ))
              )}
              
              {/* Draw boxes */}
              {boxes.map((row, rowIndex) =>
                row.map((owner, colIndex) => (
                  owner > 0 && (
                    <rect
                      key={`box-${rowIndex}-${colIndex}`}
                      x={colIndex * 64 + 40}
                      y={rowIndex * 64 + 40}
                      width="48"
                      height="48"
                      fill={owner === 1 ? "#4CAF50" : "#2196F3"}
                      opacity="0.7"
                    />
                  )
                ))
              )}
              
              {/* Draw box numbers */}
              {boxes.map((row, rowIndex) =>
                row.map((owner, colIndex) => (
                  owner > 0 && (
                    <text
                      key={`text-${rowIndex}-${colIndex}`}
                      x={colIndex * 64 + 64}
                      y={rowIndex * 64 + 68}
                      textAnchor="middle"
                      fill="white"
                      fontSize="20"
                      fontWeight="bold"
                    >
                      {owner}
                    </text>
                  )
                ))
              )}
            </svg>
          </div>
        </div>

        {gameOver && (
          <div className="game-over">
            <h2>🎉 Game Over!</h2>
            <p>Winner: {getWinner()}</p>
            <p>Final Score - Player 1: {scores.player1}, Player 2: {scores.player2}</p>
          </div>
        )}

        <div className="game-controls">
          <button className="btn btn-primary" onClick={startGame}>
            {gameOver ? 'Play Again' : 'Start Game'}
          </button>
          <Link to="/" className="btn btn-secondary">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DotsAndBoxes;